import { H2, P, B, UL, LI, Callout, Table } from "./ui";
import { TablaPlanes, TablaRecargas, CosteVideo, usePricing } from "./precios";

export const QueEsCredito = () => {
  const { data } = usePricing();
  const ref = data?.duracion_referencia_min || 15;
  return (
    <>
      <P>
        Un crédito equivale, en la práctica, a <B>un segundo de vídeo terminado</B>. Es la unidad
        con la que se mide todo: lo que tiene tu plan, lo que consume cada vídeo y lo que cuesta
        cada acción suelta.
      </P>
      <P>
        Está pensado justo para eso: para que no tengas que traducir nada. Si tu plan tiene 4.200
        créditos, tienes unos 4.200 segundos de vídeo al mes.
      </P>

      <H2 id="aproximado">Por qué «aproximadamente»</H2>
      <P>
        Un vídeo de {ref} minutos consume del orden de <CosteVideo minutos={ref} />, pero no todos
        cuestan lo mismo. Lo que hace variar el consumo es <B>cuánto cuesta encontrar el material</B>:
      </P>
      <UL>
        <LI>Un tema con mucho archivo disponible encuentra a la primera y consume poco.</LI>
        <LI>Un tema con poco archivo obliga a buscar en más fuentes y a generar imágenes, y consume bastante más.</LI>
        <LI>Un guion muy denso lleva más planos por minuto, y cada plano es una búsqueda.</LI>
      </UL>
      <P>
        La diferencia entre un vídeo barato y uno caro puede ser del doble. Por eso el número de
        vídeos de un plan es una referencia, no una promesa exacta.
      </P>

      <H2 id="en-que-se-gasta">En qué se va</H2>
      <P>
        Tres partidas se llevan la mayor parte de un vídeo:
      </P>
      <Table
        head={["Partida", "Peso aproximado"]}
        rows={[
          ["Búsqueda y verificación de material", "El grueso"],
          ["Imágenes generadas con IA", "Mucho, cuando el tema tiene poco archivo"],
          ["Narración", "Proporcional a la duración"],
          ["Guion", "Poco"],
          ["Render", "El 10% de la duración del vídeo"],
        ]}
      />
      <Callout tipo="info" titulo="El render es lo más barato">
        Montar el vídeo cuesta un 10% de su duración. Lo caro es todo lo que pasa antes: escribir,
        narrar y sobre todo buscar y comprobar el material.
      </Callout>

      <H2 id="dos-bolsas">Plan y recarga</H2>
      <P>
        Tienes dos bolsas de saldo. La del <B>plan</B> se repone cada mes, y lo que no gastaste{" "}
        <B>se arrastra al mes siguiente</B> — hasta un máximo de una mensualidad extra, así que
        nunca tienes en la mano más de dos meses de saldo. La de <B>recarga</B> es un pago único y
        no caduca mientras tengas plan activo.
      </P>
      <P>
        Se gasta primero la del plan y solo después la de recarga, para que no pierdas saldo comprado.
      </P>
    </>
  );
};

export const Planes = () => (
  <>
    <P>
      Todos los planes hacen lo mismo: cambian cuánto puedes producir, cuántos canales llevas y
      cuánto puede durar cada vídeo.
    </P>
    <TablaPlanes />

    <H2 id="elegir">Cuál te conviene</H2>
    <UL>
      <LI><B>Starter</B> — un canal publicando cada semana con vídeos cortos.</LI>
      <LI><B>Creator</B> — varios canales, o uno solo publicando a buen ritmo, con vídeos más largos.</LI>
      <LI><B>Pro</B> — producción en volumen y muchos canales.</LI>
    </UL>

    <Callout tipo="truco" titulo="Empieza corto">
      Los vídeos de 10-15 minutos consumen la mitad que los de 30 y, en canales nuevos, suelen
      retener mejor. Publicar cuatro cortos rinde más que dos largos con el mismo saldo.
    </Callout>

    <H2 id="anual">Pago anual</H2>
    <P>
      Pagando por año se cobran <B>diez meses en vez de doce</B>: dos meses salen gratis. Recibes
      las doce mensualidades completas, una cada mes, igual que en el plan mensual — lo que cambia
      es el precio, no el ritmo de entrega.
    </P>
    <Callout tipo="info" titulo="Por qué mes a mes y no todo de golpe">
      Porque lo que se reparte es capacidad de producción, no fichas en un almacén: se renderiza
      un vídeo cada vez, y un año entero de saldo soltado de una vez ocuparía la cola durante
      días. Repartido, cada uno tiene su turno. Y como el saldo no gastado se arrastra al mes
      siguiente, no pierdes nada por producir a rachas.
    </Callout>

    <H2 id="cambiar">Cambiar de plan</H2>
    <P>
      Puedes subir o bajar cuando quieras. Al cambiar, tu saldo del plan pasa al del plan nuevo en
      la siguiente renovación; el saldo de recarga no se toca.
    </P>
  </>
);

export const Recargas = () => (
  <>
    <P>
      Si un mes te quedas corto, puedes comprar créditos sueltos sin cambiar de plan.
    </P>
    <TablaRecargas />

    <H2 id="cuando">Cuándo tienen sentido</H2>
    <P>
      Para un pico puntual: un vídeo extra, una tanda de pruebas, un mes con más publicaciones de
      lo normal. Si te pasa todos los meses, sale mejor subir de plan: <B>el crédito de un plan
      siempre es más barato que el de una recarga</B>, y esa diferencia está puesta a propósito.
    </P>

    <Callout tipo="info" titulo="No caducan">
      El saldo de recarga se queda hasta que lo gastes, mientras mantengas un plan de pago activo.
      Y se consume después del saldo del plan, para que no pierdas lo comprado.
    </Callout>

    <H2 id="requisito">Hace falta plan de pago</H2>
    <P>
      Las recargas solo están disponibles con un plan activo: son saldo <B>extra</B> sobre el de
      tu plan, no una forma de usar YTMethod sin suscripción.
    </P>
  </>
);

export const Acciones = () => (
  <>
    <P>
      Además de producir vídeos, hay acciones sueltas que consumen créditos. Todas son opcionales y
      ninguna se ejecuta sin que la pidas.
    </P>

    <H2 id="lista">Qué consume y cuánto</H2>
    <Table
      head={["Acción", "Consumo aproximado"]}
      rows={[
        ["Generar un guion", "Notable: es de las partidas grandes"],
        ["Mejorar un guion con IA", "Lo mismo que generarlo: reescribe entero"],
        ["Analizar un guion pegado", "Poco"],
        ["Generar una miniatura con IA", "Poco por imagen"],
        ["Ideas de vídeos", "Muy poco"],
        ["Generar títulos de calendario", "Poco, proporcional a cuántos pidas"],
        ["Sustituir un clip", "Muy poco por clip"],
        ["Renderizar", "El 10% de la duración del vídeo"],
      ]}
    />
    <P>
      Las cifras exactas las ves en la propia aplicación antes de confirmar cada acción: siempre se
      muestra el coste estimado antes de gastar.
    </P>

    <H2 id="ahorrar">Cómo gastar menos</H2>
    <UL>
      <LI>
        <B>Revisa antes de renderizar.</B> Es gratis y evita repetir el render, que sí cuesta.
      </LI>
      <LI>
        <B>No uses «Regenerar todo» por una escena.</B> Tira material bueno ya pagado. Sustituye los
        clips concretos.
      </LI>
      <LI>
        <B>Edita el guion a mano</B> si es un cambio pequeño. «Mejorar con IA» lo reescribe entero
        y cuesta como generarlo.
      </LI>
      <LI>
        <B>Elige temas con archivo visual.</B> Es lo que más mueve la aguja: un tema sin material
        obliga a generar imágenes, que es de lo más caro.
      </LI>
      <LI>
        <B>Haz vídeos más cortos</B> mientras aprendes qué títulos funcionan.
      </LI>
    </UL>

    <Callout tipo="aviso" titulo="Si te quedas sin saldo a mitad">
      Una producción puede detenerse por falta de créditos. No se pierde lo hecho: al recargar
      puedes continuar desde donde estaba con «Completar lo que falta», sin volver a pagar lo que
      ya estaba buscado.
    </Callout>
  </>
);
